/**
 * shadcn Calendar + Popover 기반 Date / Month / Range Picker.
 * 값은 문자열로 주고받아 기존 API·폼(yyyy-MM-dd, yyyyMM)과 맞춘다.
 */
import { Button } from '@/shared/components/ui/button';
import { Calendar } from '@/shared/components/ui/calendar';
import { ClearFieldButton } from '@/shared/components/ui/clear-field-button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  formatDateDisplay,
  formatDateRangeDisplay,
  formatDateRangeValue,
  formatDateValue,
  formatRefYmDisplay,
  formatRefYmValue,
  isDateRangeSelectionComplete,
  parseDateRangeValue,
  parseDateValue,
  parseRefYmValue,
} from '@/shared/lib/datePickerUtils';
import { cn } from '@/shared/lib/utils';
import { ko } from 'date-fns/locale';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';

const triggerClassName = cn(
  'h-9 w-full justify-start gap-2 px-3 text-left text-sm font-normal',
  'border border-ruda-border-light bg-white shadow-none',
  'hover:border-ruda-border-dark hover:bg-ruda-grey-50',
);

const PickerField: React.FC<{
  className?: string;
  canClear: boolean;
  onClear: () => void;
  children: React.ReactNode;
}> = ({ className, canClear, onClear, children }) => (
  <div className={cn('relative w-full', className)}>
    {children}
    {canClear ? <ClearFieldButton onClick={onClear} /> : null}
  </div>
);

/** 드롭다운 기본은 올해 말까지라 계약 종료일 등 미래 날짜를 고를 수 없다 */
const PICKER_START_MONTH = new Date(new Date().getFullYear() - 30, 0);
const PICKER_END_MONTH = new Date(new Date().getFullYear() + 20, 11);

const pickerDropdownLabels = {
  labelMonthDropdown: () => '월 선택',
  labelYearDropdown: () => '연 선택',
};

export interface DatePickerProps {
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** 달력 caption에 연/월 드롭다운 표시 */
  captionLayout?: 'label' | 'dropdown' | 'dropdown-months' | 'dropdown-years';
  /**
   * Dialog 안에서 쓸 때 true(기본). TimePicker와 동일.
   * false면 바깥 패널(중첩 Popover)이 함께 닫힐 수 있다.
   */
  modal?: boolean;
  /** 연 드롭다운 시작 월. 기본 현재연도-30년 1월 */
  startMonth?: Date;
  /** 연 드롭다운 끝 월. 기본 현재연도+20년 12월 */
  endMonth?: Date;
  /** 선택 불가 날짜(react-day-picker Matcher). 생년월일처럼 오늘 이후를 막을 때 쓴다 */
  disabledDays?: React.ComponentProps<typeof Calendar>['disabled'];
  /** 값이 비어 있을 때 달력을 열 월. 없으면 이번 달 */
  defaultMonth?: Date;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  id,
  value,
  onChange,
  placeholder = '날짜 선택',
  disabled,
  className,
  captionLayout = 'dropdown',
  modal = true,
  startMonth = PICKER_START_MONTH,
  endMonth = PICKER_END_MONTH,
  disabledDays,
  defaultMonth,
}) => {
  const [open, setOpen] = React.useState(false);
  const selected = parseDateValue(value);
  const canClear = Boolean(value) && !disabled;

  return (
    <PickerField
      className={className}
      canClear={canClear}
      onClear={() => onChange?.('')}
    >
      <Popover modal={modal} open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              triggerClassName,
              !value && 'text-muted-foreground',
              canClear && 'pr-8',
            )}
          >
            <CalendarIcon className="size-4 shrink-0 opacity-70" />
            {value ? formatDateDisplay(value) : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={ko}
            captionLayout={captionLayout}
            startMonth={startMonth}
            endMonth={endMonth}
            selected={selected}
            defaultMonth={selected ?? defaultMonth}
            disabled={disabledDays}
            onSelect={(date) => {
              onChange?.(formatDateValue(date));
              setOpen(false);
            }}
            labels={pickerDropdownLabels}
          />
        </PopoverContent>
      </Popover>
    </PickerField>
  );
};

export interface MonthPickerProps {
  id?: string;
  /** API 기준년월 yyyyMM */
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /**
   * Dialog 안에서 쓸 때 true(기본).
   * false면 바깥 패널(중첩 Popover)이 함께 닫힐 수 있다.
   */
  modal?: boolean;
}

const MONTH_LABELS = [
  '1월',
  '2월',
  '3월',
  '4월',
  '5월',
  '6월',
  '7월',
  '8월',
  '9월',
  '10월',
  '11월',
  '12월',
] as const;

export const MonthPicker: React.FC<MonthPickerProps> = ({
  id,
  value,
  onChange,
  placeholder = '년월 선택',
  disabled,
  className,
  modal = true,
}) => {
  const [open, setOpen] = React.useState(false);
  const selected = parseRefYmValue(value);
  const [viewYear, setViewYear] = React.useState(
    () => selected?.getFullYear() ?? new Date().getFullYear(),
  );

  // selected(Date)를 의존성에 두면 매 렌더 새 참조가 되어 년도 화살표가 되돌아간다
  React.useEffect(() => {
    const year = parseRefYmValue(value)?.getFullYear();
    if (year != null) setViewYear(year);
  }, [value]);

  const selectedMonth = selected?.getMonth();
  const canClear = Boolean(value) && !disabled;

  return (
    <PickerField
      className={className}
      canClear={canClear}
      onClear={() => onChange?.('')}
    >
      <Popover modal={modal} open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              triggerClassName,
              !value && 'text-muted-foreground',
              canClear && 'pr-8',
            )}
          >
            <CalendarIcon className="size-4 shrink-0 opacity-70" />
            {value ? formatRefYmDisplay(value) : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          <div className="mb-3 flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="이전 해"
              onClick={() => setViewYear((year) => year - 1)}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="text-sm font-medium">{viewYear}년</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="다음 해"
              onClick={() => setViewYear((year) => year + 1)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {MONTH_LABELS.map((label, monthIndex) => {
              const isSelected =
                selectedMonth === monthIndex &&
                selected?.getFullYear() === viewYear;
              return (
                <Button
                  key={label}
                  type="button"
                  variant={isSelected ? 'default' : 'outline'}
                  size="sm"
                  className="h-9"
                  onClick={() => {
                    const next = formatRefYmValue(
                      new Date(viewYear, monthIndex, 1),
                    );
                    onChange?.(next);
                    setOpen(false);
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </PickerField>
  );
};

export interface DateRangePickerProps {
  id?: string;
  value?: { from?: string; to?: string };
  onChange?: (value: { from: string; to: string }) => void;
  placeholder?: string;
  disabled?: boolean;
  /** 시작일 고정 (스케줄 공지 게시 시작일 등) */
  disabledFrom?: boolean;
  /** 종료일 고정 */
  disabledTo?: boolean;
  className?: string;
  numberOfMonths?: number;
  /** 달력 caption에 연/월 드롭다운 표시 */
  captionLayout?: 'label' | 'dropdown' | 'dropdown-months' | 'dropdown-years';
  /**
   * Dialog 안에서 쓸 때 true(기본).
   * false면 바깥 패널(중첩 Popover)이 함께 닫힐 수 있다.
   */
  modal?: boolean;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  id,
  value,
  onChange,
  placeholder = '기간 선택',
  disabled,
  disabledFrom = false,
  disabledTo = false,
  className,
  numberOfMonths = 2,
  captionLayout = 'dropdown',
  modal = true,
}) => {
  const [open, setOpen] = React.useState(false);
  // shadcn range 패턴: 선택 중에는 로컬 draft를 쓰고, 완료 시에만 부모로 커밋
  const [draft, setDraft] = React.useState<DateRange | undefined>(() =>
    parseDateRangeValue(value),
  );
  // 첫 클릭이 from===to 로 들어오는 RDP 동작을 “미완료”로 구분
  const [awaitingEnd, setAwaitingEnd] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setDraft(parseDateRangeValue(value));
      setAwaitingEnd(false);
    }
  }, [value, open]);

  const displayValue = open
    ? formatDateRangeValue(draft)
    : { from: value?.from ?? '', to: value?.to ?? '' };

  const commitAndClose = (range: DateRange | undefined) => {
    const next = formatDateRangeValue(range);
    onChange?.(next);
    setAwaitingEnd(false);
    setOpen(false);
  };

  const handleSelect = (range: DateRange | undefined) => {
    // 시작일 고정 시 클릭을 종료일 선택으로만 반영 (레거시 RangePicker [true, false])
    if (disabledFrom) {
      const lockedFrom = value?.from ?? '';
      const picked = range?.to ?? range?.from;
      const to = formatDateValue(picked);
      setDraft({
        from: parseDateValue(lockedFrom),
        to: picked,
      });
      onChange?.({ from: lockedFrom, to });
      if (lockedFrom && to) {
        setAwaitingEnd(false);
        setOpen(false);
      }
      return;
    }

    if (disabledTo) {
      const lockedTo = value?.to ?? '';
      const from = formatDateValue(range?.from);
      setDraft({
        from: range?.from,
        to: parseDateValue(lockedTo),
      });
      onChange?.({ from, to: lockedTo });
      if (from && lockedTo) {
        setAwaitingEnd(false);
        setOpen(false);
      }
      return;
    }

    setDraft(range);

    if (!range?.from) {
      setAwaitingEnd(false);
      return;
    }

    // 첫 클릭(또는 재시작): from만 있거나 from===to 단일일 → 팝오버 유지
    if (!isDateRangeSelectionComplete(range, awaitingEnd)) {
      setAwaitingEnd(true);
      return;
    }

    commitAndClose(range);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraft(parseDateRangeValue(value));
      setAwaitingEnd(false);
      setOpen(true);
      return;
    }
    // 바깥 클릭 등으로 닫을 때는 미완료 draft를 버리고 커밋값으로 복구
    setDraft(parseDateRangeValue(value));
    setAwaitingEnd(false);
    setOpen(false);
  };

  const canClear =
    Boolean(displayValue.from || displayValue.to) &&
    !disabled &&
    !disabledFrom &&
    !disabledTo;

  const clearRange = () => {
    setDraft(undefined);
    setAwaitingEnd(false);
    onChange?.({ from: '', to: '' });
    setOpen(false);
  };

  return (
    <PickerField className={className} canClear={canClear} onClear={clearRange}>
      <Popover modal={modal} open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              triggerClassName,
              !(displayValue.from || displayValue.to) &&
                'text-muted-foreground',
              canClear && 'pr-8',
            )}
          >
            <CalendarIcon className="size-4 shrink-0 opacity-70" />
            {displayValue.from || displayValue.to
              ? formatDateRangeDisplay(displayValue)
              : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            locale={ko}
            captionLayout={captionLayout}
            startMonth={PICKER_START_MONTH}
            endMonth={PICKER_END_MONTH}
            numberOfMonths={numberOfMonths}
            defaultMonth={draft?.from ?? draft?.to}
            selected={draft}
            onSelect={handleSelect}
            labels={pickerDropdownLabels}
          />
          {canClear ? (
            <div className="flex justify-end border-t p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearRange}
              >
                초기화
              </Button>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </PickerField>
  );
};
